import {
  LocationStrategy,
  PathLocationStrategy,
  Location
} from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GridSettings } from 'src/app/models/grid-settings';
import moment from 'moment';
import {
  MapDataRequest,
  IBasicMapData
} from 'src/app/components/arcgis/basic-esri-map/basic-esri-map.component';
import { Subject, of, takeUntil } from 'rxjs';
import { GeomUtils } from 'src/app/components/arcgis/geom-utils';
import { WorkflowService } from 'src/app/services';
import { ActivatedRoute } from '@angular/router';
import { CommonPaginationData } from 'src/app/components/common/common-pagination/common-pagination.component.model';
import { WorkflowApplicationsJanusService } from '../workflow-applications-janus.service';
import { ItemSearchOptionFieldOption } from 'src/app/components/filter-list/models/filterClasses';
@Component({
  selector: 'app-map',
  standalone: false,
  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy }
  ],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements OnInit, OnDestroy {
  dummyFilters$ = of(null);
  @Input() manipulateURL: boolean = true;
  @Input() id: string;

  settings: GridSettings = new GridSettings({
    pageNumber: 0,
    pageSize: 10
  });
  mapDataRequest: MapDataRequest;
  selectedTab = 'data';
  departmentId: string;
  private readonly destroy$ = new Subject<void>();
  // Added by amisha on 2026-06-30 to reuse the Janus table's active filter payload on the map tab.
  // Reason: map results must stay aligned with data-table API filtering instead of always loading unfiltered applications.
  private activeFilterInputs: ItemSearchOptionFieldOption[] = [];
  private lastFilterSignature = '';
  private lastDepartmentId = '';
  constructor(
    private location: Location,
    private workflowService: WorkflowService,
    private route: ActivatedRoute,
    private workflowApplicationsJanusSvc: WorkflowApplicationsJanusService
  ) {}

  paginationData: CommonPaginationData = {
    variant: 'staff',
    totalPages: 0,
    currentPage: 0,
    pageSize: 20,
    pageSizeOptions: [10, 25, 50, 100],
    showRowsPerPage: true,
    showTotalRecords: true,
    visiblePages: [],
    canGoPrevious: false,
    canGoNext: false
  };

  pageNumber = 1;
  maxRows = 20;
  // ✅ Map extent state
  startingMapVariables: {
    mapExtent: {
      xmax: number;
      ymax: number;
      xmin: number;
      ymin: number;
    };
    mapRequests: number;
  } = { mapExtent: null, mapRequests: 0 };

  // 🔥 Popup Title
  popupTemplateTitle: string = 'Application ID: {applicationNumber}';

  // 🔥 Popup Content (HTML allowed)
  popupTemplateContent: string = `
<b>Type:</b> {permitType}<br/>
<b>Description:</b> {description}<br/>
<b>Applicant:</b> {applicantsName}<br/>
<b>Created:</b> {dateCreated}<br/>
<b>Status:</b> {status}
`;

  // 🔥 Optional popup action buttons
  popupActions = [
    {
      title: 'View Application',
      id: 'view-application',
      className: 'esri-icon-zoom-out-magnifying-glass',
      handler: (attributes: any) => {
        // later yaha routing karenge
        // this.router.navigate(['/map/application', attributes.applicationNumber]);
      }
    },
    GeomUtils.CopyCoordinatesToClipboardAction,
    GeomUtils.NavigateToGoogleAction,
    GeomUtils.NavigateToBingAction
  ];

  // -----------------------------------------
  // EXTENT CHANGE (Already working logic)
  // -----------------------------------------
  mapExtentChanged(extentChangedValue: {
    xmax: number;
    ymax: number;
    xmin: number;
    ymin: number;
  }) {
    this.startingMapVariables.mapExtent = extentChangedValue;
    this.setURLFromSettings();
  }

  // -----------------------------------------
  // 🔥 MAIN METHOD — MAP DATA REQUEST HANDLE
  // -----------------------------------------
  // onMapDataRequested(request: MapDataRequest) {

  //   const dummyData: IBasicMapData[] = [
  //     {
  //       geometryWkts: ['POINT(-95.3698 29.7604)'],
  //       data: {
  //         applicationNumber: 'TEST-001',
  //         permitType: 'Testing',
  //         description: 'Dummy data from MapComponent',
  //         applicantsName: 'Console Test',
  //         dateCreated: new Date(),
  //         status: 'Working'
  //       }
  //     },
  //     {
  //       geometryWkts: ['POINT(-95.3705 29.7615)'],
  //       data: {
  //         applicationNumber: 'TEST-002',
  //         permitType: 'Verification',
  //         description: 'Second dummy point',
  //         applicantsName: 'Flow Check',
  //         dateCreated: new Date(),
  //         status: 'Success'
  //       }
  //     }
  //   ];

  //   console.log('🚀 Sending Dummy Data To Map:', dummyData);

  //   // ⚠️ THIS LINE RETURNS DATA BACK TO MAP
  //   request.gimmeTheData$.emit(dummyData);
  // }

  onMapDataRequested(request: MapDataRequest) {
    this.lastRequest = request;
    const departmentId = this.route.parent?.parent?.snapshot.paramMap.get(
      'departmentId'
    );

    if (!departmentId) {
      console.error('departmentId not found');
      request.gimmeTheData$.emit([]);
      return;
    }

    this.departmentId = departmentId;

    // Updated by amisha on 2026-06-30 to send the same shared Janus filters that the data table sends to the applications API.
    // Reason: switching from the data tab to the map tab should preserve search, advanced filters, and attribute filters.
    this.workflowService
      .getDepartmentApplications(departmentId, {
        pageNumber: this.pageNumber,
        maxRows: this.maxRows,
        inputValues: this.activeFilterInputs
      })
      .subscribe(data => {
        const totalApplications =
          data?.dataSetResult?.totalApplicationCount || 0;

        const totalPages = Math.ceil(totalApplications / this.maxRows);
        this.paginationData = {
          ...this.paginationData,
          totalPages,
          currentPage: this.pageNumber - 1,
          pageSize: this.maxRows,
          canGoPrevious: this.pageNumber > 1,
          canGoNext: this.pageNumber < totalPages
        };

        this.paginationData.visiblePages = this.getVisiblePages();

        const rows = data?.dataSetResult?.rows || [];

        const rowsWithGeometry = rows.filter(
          (app: any) => !!app?.values?.geometrY_WKT
        );

        const appsMapped = rowsWithGeometry.map((app: any) => ({
          geometryWkts: [app.values.geometrY_WKT],
          data: {
            appId: app.id,
            applicantsName: app.values?.['applicant'],
            applicationNumber: app.values?.['app #'],
            dateCreated: app.values?.['app Date']
              ? moment(app.values['app Date']).format('MM/DD/YYYY hh:mm A')
              : '',
            description: app.description,
            permitType: app.values?.['app Type'],
            status: app.values?.['status']
          }
        }));

        request.gimmeTheData$.emit(appsMapped);
      });
  }
  getVisiblePages(): (number | 'dots')[] {
    const totalPages = this.paginationData.totalPages;
    const currentPage = this.paginationData.currentPage;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages: (number | 'dots')[] = [];

    pages.push(0);

    if (currentPage > 3) {
      pages.push('dots');
    }

    for (
      let i = Math.max(1, currentPage - 1);
      i <= Math.min(totalPages - 2, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }

    if (currentPage < totalPages - 4) {
      pages.push('dots');
    }

    pages.push(totalPages - 1);

    return pages;
  }
  // -----------------------------------------
  // URL Handling (Lightweight version)
  // -----------------------------------------
  setURLFromSettings() {
    if (!this.manipulateURL || !this.startingMapVariables.mapExtent) return;

    const ext = this.startingMapVariables.mapExtent;

    const params = `${ext.xmax}|${ext.ymax}|${ext.xmin}|${ext.ymin}|${this.selectedTab}`;

    const urlpath = location.pathname + `?map=${params}`;

    this.location.replaceState(urlpath);
  }

  onPageSelect(page: number): void {
    this.pageNumber = page + 1;
    this.reloadMapData();
  }

  onNext(): void {
    this.pageNumber++;
    this.reloadMapData();
  }

  onPrevious(): void {
    this.pageNumber--;
    this.reloadMapData();
  }

  onFirst(): void {
    this.pageNumber = 1;
    this.reloadMapData();
  }

  onLast(): void {
    this.pageNumber = this.paginationData.totalPages;
    this.reloadMapData();
  }

  onPageSizeChange(size: number): void {
    this.maxRows = size;
    this.pageNumber = 1;
    this.reloadMapData();
  }
  private lastRequest: MapDataRequest;

  ngOnInit(): void {
    // Added by amisha on 2026-06-30 to watch the shared Janus filter state and refresh the map when those filters change.
    // Reason: the map component does not own the filter builder, so it must listen to the shared service to stay in sync with workflow applications.
    this.workflowApplicationsJanusSvc.data$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        const nextFilterInputs = this.getCombinedFilterInputs(data);
        const nextSignature = JSON.stringify(nextFilterInputs);

        this.activeFilterInputs = nextFilterInputs;

        if (this.lastFilterSignature !== nextSignature) {
          this.lastFilterSignature = nextSignature;
          this.pageNumber = 1;
          this.reloadMapData();
        }
      });

    // Added by amisha on 2026-06-30 to reset map paging when the active department changes.
    // Reason: each department has its own application dataset, so the map should refetch from page 1 after a department switch.
    this.route.parent?.parent?.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const departmentId = params.get('departmentId') || '';
        if (!departmentId || this.lastDepartmentId === departmentId) {
          return;
        }

        this.lastDepartmentId = departmentId;
        this.departmentId = departmentId;
        this.pageNumber = 1;
        this.reloadMapData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadMapData(): void {
    if (this.lastRequest) {
      this.onMapDataRequested(this.lastRequest);
    }
  }

  // Added by amisha on 2026-06-30 to flatten the shared Janus filter sections into the API input format expected by department applications.
  // Reason: the map and data table both need one merged `inputValues` array built from external filters, attribute filters, and search filters.
  private getCombinedFilterInputs(data: {
    filterInputs?: Array<{ options?: ItemSearchOptionFieldOption[] }>;
    attributeFilterInputs?: Array<{ options?: ItemSearchOptionFieldOption[] }>;
    searchText?: Array<{ options?: ItemSearchOptionFieldOption[] }>;
  }): ItemSearchOptionFieldOption[] {
    return [
      ...(data.filterInputs?.flatMap(filter => filter.options || []) || []),
      ...(data.attributeFilterInputs?.flatMap(filter => filter.options || []) ||
        []),
      ...(data.searchText?.flatMap(filter => filter.options || []) || [])
    ];
  }
}
